import { useState } from 'react';

/**
 * Marca de Copathy.
 *
 * Dos piezas distintas, y usarlas al revés se nota:
 * · `logotipo` — la palabra completa con EXPERIENCE. Va donde hay ancho.
 * · `isotipo`  — solo la figura, en su círculo marino. Va donde no lo hay:
 *                el menú plegado, la pestaña, un avatar.
 *
 * Los archivos viven en `web/public/` y los recorta
 * `scripts/preparar-logos.mjs`. Si falta alguno se cae a un marcador — que
 * **no es la marca**, solo evita que la pantalla se vea rota:
 *
 *   copathy-logo.png        logotipo a color, para fondo claro
 *   copathy-logo-claro.png  logotipo blanco y naranja, para fondo marino
 *   copathy-isotipo.png       isotipo a color (círculo marino, figura naranja)
 *   copathy-isotipo-claro.png solo la figura naranja, sin círculo, para fondo marino
 */
type Props = {
  tamano?: 'sm' | 'md' | 'lg' | 'xl';
  /** `claro` para fondos oscuros. */
  tono?: 'color' | 'claro';
  variante?: 'logotipo' | 'isotipo';
};

/*
  Alturas del lockup completo (logotipo + EXPERIENCE), proporción ~2.82.

  ⚠️ `xl` es el techo, y no por gusto: el archivo tiene 600 px de ancho, asi que
  a 96 px de alto mide 271 px y en una pantalla @2x pide 542 px reales. Queda
  justo. Por encima de eso hay que regenerar el archivo desde el original de
  2048 px —`marca-originales/`— en vez de estirar este.
*/
const ALTURAS = { sm: 28, md: 44, lg: 68, xl: 96 } as const;
// El isotipo es cuadrado, así que se le da menos alto para que pese igual.
const ALTURAS_ISOTIPO = { sm: 34, md: 44, lg: 56, xl: 72 } as const;

export function Marca({ tamano = 'md', tono = 'color', variante = 'logotipo' }: Props) {
  const [falla, setFalla] = useState(false);
  const esIsotipo = variante === 'isotipo';
  const alto = esIsotipo ? ALTURAS_ISOTIPO[tamano] : ALTURAS[tamano];

  const archivo = esIsotipo
    ? tono === 'claro'
      ? '/copathy-isotipo-claro.png'
      : '/copathy-isotipo.png'
    : tono === 'claro'
      ? '/copathy-logo-claro.png'
      : '/copathy-logo.png';

  if (!falla) {
    /*
      El `<span>` no es decorativo: **evita que flexbox deforme el logotipo.**

      Un `<img>` hijo directo de un contenedor `flex flex-col` recibe
      `align-self: stretch` —el valor por omision— y eso le estira el ANCHO al
      del contenedor. `w-auto` no alcanza: `stretch` gana sobre `width: auto`.
      Con la altura fijada aca, el resultado es el logotipo aplastado a lo ancho.
      Paso de verdad en `PanelPresentacion`: se midieron 707x68 px sobre un
      archivo de proporcion 2.80, o sea estirado 3,7 veces.

      Envuelto, el que se estira es el `span` —que no pinta nada— y la imagen de
      adentro ya no es item flex, asi que conserva su proporcion. Se arregla aca,
      una vez, en vez de acordarse de `self-start` en cada uso.
    */
    return (
      <span className="inline-flex shrink-0">
        <img
          src={archivo}
          alt="COPATHY Experience"
          height={alto}
          style={{ height: alto }}
          /*
            El ancho lo pone la proporcion real del archivo.

            `max-w-none` desactiva el `max-width: 100%` que Tailwind le pone a
            toda imagen: con la altura fija, ese tope no encoge el logotipo, lo
            APLASTA. En un contenedor demasiado angosto es mejor que desborde y
            lo recorte el padre, que deformar la marca.

            Sin `rounded-full` a proposito -- el isotipo YA trae su circulo, y una
            mascara redonda sobre una imagen que no es exactamente cuadrada
            (la clara mide 254x256) la convertiria en una elipse.
          */
          className="w-auto max-w-none select-none"
          onError={() => setFalla(true)}
        />
      </span>
    );
  }

  /*
    Marcador mientras no esté el archivo. La forma es la del isotipo —un
    círculo— para que al llegar el real no cambie el peso del bloque. Una letra
    sobre el color de la marca es el mismo recurso que usa Polaris cuando un
    producto no tiene isotipo propio.
  */
  if (esIsotipo) {
    return (
      <span
        style={{ width: alto, height: alto }}
        className="grid shrink-0 place-items-center rounded-full bg-marino-700 font-extrabold text-naranja-500 select-none"
        title="Falta web/public/copathy-isotipo.png"
      >
        C
      </span>
    );
  }

  const escalas = {
    sm: { titulo: 'text-2xl', sub: 'text-[0.5rem] tracking-[0.42em]' },
    md: { titulo: 'text-4xl', sub: 'text-[0.6rem] tracking-[0.52em]' },
    lg: { titulo: 'text-5xl', sub: 'text-xs tracking-[0.55em]' },
    xl: { titulo: 'text-6xl', sub: 'text-sm tracking-[0.58em]' },
  } as const;
  const e = escalas[tamano];

  return (
    <div className="inline-flex flex-col items-start leading-none select-none">
      <div className={`${e.titulo} font-extrabold tracking-tight`}>
        <span className="text-naranja-500">CO</span>
        <span className={tono === 'claro' ? 'text-white' : 'text-marino-700'}>PATHY</span>
      </div>
      <div className={`${e.sub} mt-1.5 font-bold text-naranja-500 uppercase`}>Experience</div>
    </div>
  );
}
